import { Fragment, useEffect, useLayoutEffect, useRef, useState, ViewTransition } from "react";
import { Outlet, useLocation } from "react-router";
import { APP_NAME } from "../../shared/config";
import { useTrackHistory } from "../../shared/phone/history";
import { usePhone, useRail } from "../../shared/phone/media";
import { TitleEnd } from "../../shared/phone/PhoneNav";
import { swipe, useEdgeSwipeBack, useInstallable, usePhoneViewport, usePullToRefresh, useScrollMemory } from "../../shared/phone/shell";
import { IN_APP } from "../lib/inApp";
import { lastLoaded } from "../lib/kept";
import { prefetchGoal } from "../views/cc/GoalRoutes";
import { warmHome } from "../views/cc/useCc";
import { prefetchLibraryItem } from "../views/library/ItemNative";
import { warmShelf } from "../views/library/useShelf";
import { prefetchPartner } from "../views/partners/usePartnerDetail";
import { prefetchProduct } from "../views/products/useProductPage";
import { warmProducts } from "../views/products/useProducts";
import { FlashsuiteBar, FlashsuiteMenu, useFlashsuiteScript } from "./FlashsuiteMenu";
import { useAppBridge } from "./phone/appBridge";
import { refreshBadges, useBadges } from "./phone/badges";
import { Rail } from "./phone/Rail";
import { slideBetween } from "./phone/slide";
import { TabBar } from "./phone/TabBar";
import { isTabRoot } from "./phone/tabs";
import { TopBar } from "./TopBar";

/**
 * Authenticated app frame, under Flashsuite's bar (it shows only when the business's payment failed). On a
 * computer: the top bar over the routed page. On an iPad: the labelled rail
 * down the left. On a phone: the tabs along the bottom, shown on each tab's own screen. A phone and an iPad
 * draw the native screens, and the app scrolls inside `.pn-main`, so the bars stay put and the keyboard
 * never covers them.
 */
export function Shell() {
  useTrackHistory();
  usePhoneViewport();
  useEdgeSwipeBack();
  useInstallable(APP_NAME);
  const phone = usePhone();
  const rail = useRail();
  const framed = phone || rail;
  const main = useRef<HTMLElement>(null);
  useScrollMemory(main, isTabRoot);
  useOpenEarly();
  useAppBridge();
  const { pathname } = useLocation();
  const slide = useSlide(pathname, phone);
  const badges = useBadges(framed);
  useFlashsuiteScript();
  // A pull to refresh draws the screen again from scratch, so it loads afresh.
  const [epoch, setEpoch] = useState(0);
  const pullRing = usePullToRefresh(() => {
    setEpoch((n) => n + 1);
    refreshBadges();
  });

  // Why: page styles draw the native screens under .pn-app, and only the phone's or the iPad's under .pn-ph or .pn-ip.
  const cls = !framed ? "app-frame" : phone ? (isTabRoot(pathname) ? "pn-app pn-ph pn-tabbed" : "pn-app pn-ph") : "pn-app pn-ip";
  return (
    <div className={cls}>
      <FlashsuiteBar />
      <TopBar />
      <Rail badges={badges} />
      <ViewTransition update={slide} default="none">
        <main className="pn-main" ref={main}>
          {/* Why only a phone: an iPad keeps the menu at the foot of its rail. */}
          <TitleEnd.Provider value={phone ? <FlashsuiteMenu compact /> : null}>
            <Fragment key={epoch}>
              <Outlet />
            </Fragment>
          </TitleEnd.Provider>
        </main>
      </ViewTransition>
      <TabBar badges={badges} />
      <div className="pn-pull" ref={pullRing} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
        </svg>
      </div>
    </div>
  );
}

/** The slide for this move on a phone, animated by React's view transitions, which the router's navigations start. */
function useSlide(pathname: string, phone: boolean): string {
  const before = useRef(pathname);
  useLayoutEffect(() => {
    before.current = pathname;
    swipe.back = false;
  }, [pathname]);
  useEffect(() => {
    // Why: the browser's own swipe back (Safari, Chrome on Android) has already moved the page.
    const pop = (e: PopStateEvent) => {
      if (e.hasUAVisualTransition) swipe.back = true;
    };
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);
  return phone && !swipe.back ? slideBetween(before.current, pathname, isTabRoot) : "none";
}

const TABS_AFTER_MS = 1500;
/** How long a finger rests on a row before its page starts loading: a finger that scrolls the list moves sooner. */
export const HOLD_MS = 120;

/** What pressing on `target` would open, as the load that gets it ready; null when it opens nothing. */
function opening(target: EventTarget | null): (() => void) | null {
  const link = target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
  const path = link ? new URL(link.href, window.location.href).pathname : "";
  const [, kind, id] = path.split("/").map(decodeURIComponent);
  if (!id || id === "new") return null;
  if (kind === "goals") return () => prefetchGoal(id);
  if (kind === "products") return () => prefetchProduct(id);
  if (kind === "partners") return () => prefetchPartner(id);
  // Why only once: a version's words never change.
  if (kind === "library" && id !== "lessons" && !lastLoaded(`library-item:${id}`)) return () => prefetchLibraryItem(id);
  return null;
}

/**
 * A goal, product, partner or library item starts loading as it is pressed, so it opens ready: at once under a
 * mouse, and under a finger once it rests, since a finger that scrolls the list opens nothing. The tabs' own
 * screens load a moment after the app opens, so the first tap on each opens ready too.
 */
function useOpenEarly(): void {
  useEffect(() => {
    let resting = 0;
    const down = (e: PointerEvent) => {
      const open = opening(e.target);
      if (!open) return;
      if (e.pointerType === "touch") resting = window.setTimeout(open, HOLD_MS);
      else open();
    };
    // Why: the browser cancels a touch the moment it turns into a scroll.
    const scrolled = () => window.clearTimeout(resting);
    document.addEventListener("pointerdown", down, { capture: true, passive: true });
    document.addEventListener("pointercancel", scrolled, { capture: true, passive: true });
    // Why after a wait: the screen the person opened loads first; the tabs follow while they read it. Inside the
    // phone app every tab is its own screen with its own memory, so this one has no other tabs to ready.
    const tabs = IN_APP
      ? 0
      : window.setTimeout(() => {
          warmHome();
          warmProducts();
          warmShelf();
        }, TABS_AFTER_MS);
    return () => {
      document.removeEventListener("pointerdown", down, { capture: true });
      document.removeEventListener("pointercancel", scrolled, { capture: true });
      window.clearTimeout(resting);
      window.clearTimeout(tabs);
    };
  }, []);
}
